import { createInertiaApp } from '@inertiajs/react'

// Variable-weight self-hosted fonts. Inter for the UI sans;
// JetBrains Mono for tabular figures, code-shaped strings (UUIDs,
// account codes, ledger sequence numbers), and the command-palette
// input. Both ship as one woff2 each because variable-weight axes
// collapse the per-weight files.
//
// Imported here rather than via @import in the CSS so Vite hashes
// the woff2 files and emits them as discrete assets — keeps cache
// busting tight when fonts update.
import '@fontsource-variable/inter'
import '@fontsource-variable/jetbrains-mono'

import '../styles/application.css'

// Inertia 3 auto-mounts when `setup` is omitted — it detects the
// `<div id="app">` Inertia root, reads the initial page from the
// companion `<script data-page="app" type="application/json">`
// element (see `use_script_element_for_initial_page` in
// config/initializers/inertia_rails.rb), and hydrates.
createInertiaApp({
  // Lazy, not eager. An eager glob imports every page component on
  // every page load, so the sign-in form pulled the whole
  // application — 266 pages plus their transitive imports, served
  // in development as one HTTP request per module. Chrome opens
  // enough connections per origin to absorb that; Safari's pool is
  // far smaller, so it serialised the fetches and saturated the
  // main thread. Lazily, a page loads its own subgraph and nothing
  // else, and production splits per-page chunks instead of one
  // bundle carrying all of them.
  resolve: (name) => {
    // Exclude `*.test.tsx` co-located with pages — Vitest mock
    // calls (`vi.mock(...)`) execute at module load and explode in
    // the browser ("Vitest mocker was not initialized") if a test
    // file ends up in the production bundle.
    const pages = import.meta.glob<{ default: React.ComponentType<unknown> }>([
      '../pages/**/*.tsx',
      '!../pages/**/*.test.tsx',
    ])
    const page = pages[`../pages/${name}.tsx`]
    if (!page) {
      throw new Error(`Inertia page not found: ${name}`)
    }
    // Inertia accepts a promise here and awaits it before render.
    return page().then((module) => module.default)
  },
})
